// src/pages/RoleManagement.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import { Tooltip, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";

// Layout & tables
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Services & permissions
import CrudService from "services/cruds-service";
import getId from "services/helper-service";
import { AbilityContext, Can } from "Can";
import { useAbility } from "@casl/react";
import routes from "../../routes";
import { appConfirm } from "components/AppDialog";
import { buildPageOptions } from "./pages";

// routes.js’den türeyen sayfa tanımlarınız
const chunk = (arr, size) =>
    arr.reduce((chunks, item) => {
      const last = chunks[chunks.length - 1];
      if (!last || last.length === size) chunks.push([item]);
      else last.push(item);
      return chunks;
    }, []);
export default function RoleManagement() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);

  const [data, setData] = useState([]);
  const [isDemo, setIsDemo] = useState(false);
  const [notification, setNotification] = useState({ value: false, text: "" });

  // derive all pages from routes.js
  const pagesOptions = useMemo(() => buildPageOptions(routes), []);

  // fetch roles
  useEffect(() => {
    (async () => {
      const res = await CrudService.getRoles();
      setData(res.data);
      setIsDemo(process.env.REACT_APP_IS_DEMO === "true");
    })();
  }, []);

  // show navigation notifications
  useEffect(() => {
    if (state) setNotification({ value: state.value, text: state.text });
  }, [state]);

  useEffect(() => {
    if (notification.value) {
      const t = setTimeout(() => setNotification({ value: false, text: "" }), 5000);
      return () => clearTimeout(t);
    }
  }, [notification]);

  const clickAdd = () => navigate("/role-management/new-role");
  const clickEdit = (id) => navigate(`/role-management/edit-role/${id}`);
  const clickDelete = async (e, id) => {
    if (!await appConfirm("Are you sure you want to delete this role?")) {
      e.nativeEvent.stopImmediatePropagation();
      return;
    }
    try {
      await CrudService.deleteRole(id);
      const res = await CrudService.getRoles();
      setData(res.data);
      setNotification({ value: true, text: "Role successfuly deleted" });
    } catch (err) {
      console.error(err);
      const msg = err?.errors?.[0]?.detail || "Silme sırasında hata oluştu";
      setNotification({ value: true, text: msg });
    }
  };

  // helper: path’i sayfa adına çevir
  const getPageLabel = (path) => {
    const opt = pagesOptions.find((o) => o.value === path);
    return opt ? opt.label : path;
  };

  // build rows, formatting created_at in Europe/Istanbul time zone as dd/mm/yyyy hh:mm
  const rows = data.map((r) => {
    const dt = new Date(r.attributes.created_at);
    const formatted = dt.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Europe/Istanbul",
    });
    return {
      id: r.id,
      name: r.attributes.name,
      created_at: formatted,
      allowedPages: r.attributes.allowedPages || [],
    };
  });

  const dataTableData = {
    columns: [
      {
        Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Name</span>,
        accessor: "name",
        width: "25%",
      },
      {
        Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Pages</span>,
        accessor: "allowedPages",
        width: "40%",
        Cell: ({ cell: { value } }) => (
            <MDBox display="flex" flexDirection="column" gap={1}>
              {chunk(value, 3).map((group, i) => (
                  <MDBox key={i} display="flex" gap={1}>
                    {group.map((route) => (
                        <Chip key={route} label={getPageLabel(route)} size="small" />
                    ))}
                  </MDBox>
              ))}
            </MDBox>
        ),
      },
      {
        Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Created At</span>,
        accessor: "created_at",
        width: "20%",
      },
      {
        Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Actions</span>,
        disableSortBy: true,
        accessor: "actions",
        Cell: ({ row }) => (
            <MDBox display="flex" alignItems="center">
              <Can I="delete" a="roles">
                {!isDemo || getId(row.original.id) > 3 ? (
                    <Tooltip title="Delete Role">
                      <IconButton onClick={(e) => clickDelete(e, row.original.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                ) : null}
              </Can>
              {ability.can("edit", "roles") && (
                  <Tooltip title="Edit Role">
                    <IconButton onClick={() => clickEdit(row.original.id)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
              )}
            </MDBox>
        ),
      },
    ],
    rows,
  };

  return (
      <DashboardLayout>
        <DashboardNavbar />

        {notification.value && (
            <MDAlert color="info" my="20px">
              <MDTypography variant="body2" color="white">
                {notification.text}
              </MDTypography>
            </MDAlert>
        )}

        <MDBox pt={6} pb={3}>
          <MDBox p={3} display="flex" justifyContent="space-between">
            <MDTypography variant="h5">Role Management</MDTypography>
            {ability.can("create", "roles") && (
                <MDButton variant="gradient" color="dark" size="small" onClick={clickAdd}>
                  + Add Role
                </MDButton>
            )}
          </MDBox>
          <Card>
            <DataTable table={dataTableData} />
          </Card>
        </MDBox>

        <Footer />
      </DashboardLayout>
  );
}
